import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Swords, Car, X, ChevronRight, Zap } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const CrewLoadout = ({ gameId, game, crew, onClose, onRefresh }) => {
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [equipping, setEquipping] = useState(false);
  const [inventory, setInventory] = useState([]);

  const era = String(game?.current_era || 1);
  const weapons = game?.equipped_weapons?.[era] || {};
  const vehicles = game?.equipped_vehicles?.[era] || {};

  // Fetch inventory from dedicated endpoint (has category normalization)
  const loadInventory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/store/inventory/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setInventory(data.inventory || []);
      }
    } catch {}
  }, [gameId]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  // Filter equippable items from fetched inventory
  const weaponItems = inventory.filter(i => i.category === 'weapons' || i.category === 'weapon');
  const vehicleItems = inventory.filter(i => i.category === 'vehicles' || i.category === 'vehicle');
  const gearItems = inventory.filter(i => i.category === 'gear' || i.category === 'equipment' || i.category === 'armor');

  const recruitedCrew = (crew || []).filter(c => c.recruited);

  const equipItem = async (itemIdx, crewId) => {
    setEquipping(true);
    try {
      const res = await fetch(`${API}/api/store/inventory/use`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, item_index: itemIdx, crew_target: crewId }),
      });
      if (res.ok) {
        await loadInventory();
        onRefresh();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to equip');
      }
    } catch {}
    setEquipping(false);
  };

  const RARITY_COLORS = {
    common: '#6B7280', uncommon: '#4A7AB5', rare: '#9F7AEA',
    epic: '#FF6B35', legendary: '#FFD700', mythic: '#EF4444',
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="crew-loadout">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-gradient-to-b from-[#111820] to-[#0a0e12] border border-[#C9A84C]/20 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#111820] border-b border-[#1A2535] p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#C9A84C]">CREW LOADOUT</h2>
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">EQUIP WEAPONS, VEHICLES & GEAR TO YOUR CREW</p>
          </div>
          <button onClick={onClose} className="p-2 text-[#E8E0D0]/30 hover:text-[#E8E0D0] transition-colors" data-testid="close-loadout">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Crew members */}
          <div className="space-y-2">
            {recruitedCrew.length === 0 ? (
              <p className="text-center text-[#E8E0D0]/20 font-['Outfit'] text-sm py-8">No recruited crew members</p>
            ) : recruitedCrew.map(c => {
              const crewWeapon = weapons[c.id];
              const crewVehicle = vehicles[c.id];
              const isSelected = selectedCrew === c.id;

              return (
                <div key={c.id}>
                  <button
                    onClick={() => setSelectedCrew(isSelected ? null : c.id)}
                    className={`w-full bg-[#0a0e12] rounded-xl p-4 text-left transition-all ${isSelected ? 'border-2 border-[#C9A84C]/40' : 'border border-[#1A2535] hover:border-[#C9A84C]/20'}`}
                    data-testid={`loadout-crew-${c.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center">
                          <span className="font-['Cormorant_Garamond'] text-lg font-bold text-[#C9A84C]">{c.name?.[0]}</span>
                        </div>
                        <div>
                          <h4 className="font-['Outfit'] text-sm text-[#E8E0D0] font-semibold">{c.name}</h4>
                          <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">ATK {c.attack} / DEF {c.defense}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Equipped items */}
                        {crewWeapon && (
                          <span className="font-['Outfit'] text-[8px] px-2 py-1 rounded bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 text-[#8B1A1A]">
                            <Swords size={10} className="inline mr-1" />{crewWeapon.name}
                          </span>
                        )}
                        {crewVehicle && (
                          <span className="font-['Outfit'] text-[8px] px-2 py-1 rounded bg-[#4A7AB5]/10 border border-[#4A7AB5]/20 text-[#4A7AB5]">
                            <Car size={10} className="inline mr-1" />{crewVehicle.name}
                          </span>
                        )}
                        <ChevronRight size={16} className={`text-[#E8E0D0]/20 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                  </button>

                  {/* Expanded equip panel */}
                  <AnimatePresence>
                    {isSelected && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="bg-[#050505] border border-[#1A2535] border-t-0 rounded-b-xl p-4 space-y-3">
                          {/* Weapons */}
                          <div>
                            <p className="font-['Outfit'] text-[9px] tracking-widest text-[#8B1A1A] mb-2 flex items-center gap-1">
                              <Swords size={10} /> WEAPONS ({weaponItems.length} in inventory)
                            </p>
                            {weaponItems.length === 0 ? (
                              <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[10px]">No weapons in inventory. Buy from the store.</p>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {weaponItems.map(item => (
                                  <button key={item.index} onClick={() => equipItem(item.index, c.id)} disabled={equipping}
                                    className="bg-[#111820] border border-[#1A2535] hover:border-[#8B1A1A]/30 rounded-lg p-2 text-left transition-all disabled:opacity-30"
                                    data-testid={`equip-weapon-${item.index}`}>
                                    <p className="font-['Outfit'] text-xs text-[#E8E0D0]" style={{ color: RARITY_COLORS[item.rarity] || '#E8E0D0' }}>{item.name}</p>
                                    <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/25">{item.effects}</p>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Vehicles */}
                          <div>
                            <p className="font-['Outfit'] text-[9px] tracking-widest text-[#4A7AB5] mb-2 flex items-center gap-1">
                              <Car size={10} /> VEHICLES ({vehicleItems.length} in inventory)
                            </p>
                            {vehicleItems.length === 0 ? (
                              <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[10px]">No vehicles in inventory. Buy from the store.</p>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {vehicleItems.map(item => (
                                  <button key={item.index} onClick={() => equipItem(item.index, c.id)} disabled={equipping}
                                    className="bg-[#111820] border border-[#1A2535] hover:border-[#4A7AB5]/30 rounded-lg p-2 text-left transition-all disabled:opacity-30"
                                    data-testid={`equip-vehicle-${item.index}`}>
                                    <p className="font-['Outfit'] text-xs" style={{ color: RARITY_COLORS[item.rarity] || '#E8E0D0' }}>{item.name}</p>
                                    <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/25">{item.effects}</p>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Gear */}
                          {gearItems.length > 0 && (
                            <div>
                              <p className="font-['Outfit'] text-[9px] tracking-widest text-[#4ADE80] mb-2 flex items-center gap-1">
                                <Shield size={10} /> GEAR ({gearItems.length})
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {gearItems.map(item => (
                                  <button key={item.index} onClick={() => equipItem(item.index, c.id)} disabled={equipping}
                                    className="bg-[#111820] border border-[#1A2535] hover:border-[#4ADE80]/30 rounded-lg p-2 text-left transition-all disabled:opacity-30">
                                    <p className="font-['Outfit'] text-xs" style={{ color: RARITY_COLORS[item.rarity] || '#E8E0D0' }}>{item.name}</p>
                                    <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/25">{item.effects}</p>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Equipment effects info */}
          <div className="bg-[#0a0e12] rounded-xl p-4 border border-[#1A2535]">
            <p className="font-['Outfit'] text-[9px] tracking-widest text-[#C9A84C]/40 mb-2">EQUIPMENT EFFECTS</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <Swords size={14} className="text-[#8B1A1A] mx-auto mb-1" />
                <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">Each weapon</p>
                <p className="font-['Outfit'] text-[10px] text-[#8B1A1A]">+3% Jobs +10 ATK</p>
              </div>
              <div>
                <Car size={14} className="text-[#4A7AB5] mx-auto mb-1" />
                <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">Each vehicle</p>
                <p className="font-['Outfit'] text-[10px] text-[#4A7AB5]">+2% Jobs +8 DEF</p>
              </div>
              <div>
                <Shield size={14} className="text-[#4ADE80] mx-auto mb-1" />
                <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">Each gear</p>
                <p className="font-['Outfit'] text-[10px] text-[#4ADE80]">+Bonus effects</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CrewLoadout;
